import React, { useEffect, useReducer, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { FaFileExcel, FaPlus } from 'react-icons/fa';
import { MdOutlineMoney } from 'react-icons/md';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from '../../toasts';
import * as XLSX from 'xlsx';
import FilterForm from '../../components/FilterForm';
import {
	Button,
	DataTable,
	EntityList,
	ExportModal,
	IdTile,
	ListToolbar,
	PageHead,
	Pill,
	RowActionsMenu,
} from '../../components/EntityList';
import { useLabels } from '../../hooks/useLabels';
import { usePermissions } from '../../hooks/usePermissions';
import DebtConceptService from '../../services/debtConcept.service';
import DebtService from '../../services/debt.service';
import PaymentService from '../../services/payment.service';
import { getError, parsePeriodicity, showCurrency } from '../../utils';
import Paginator from '../Paginator/Paginator';

const reducer = (state, action) => {
	switch (action.type) {
	case 'FETCH_REQUEST':
		return { ...state, loading: true };
	case 'FETCH_SUCCESS':
		return {
			...state,
			debts: action.payload.debts,
			page: action.payload.page,
			pages: action.payload.pages,
			countDebts: action.payload.countDebts,
			pageSize: action.payload.pageSize,
			loading: false,
		};
	case 'FETCH_FAIL':
		return { ...state, loading: false, error: action.payload };
	case 'DELETE_REQUEST':
		return { ...state, loadingDelete: true, successDelete: false };
	case 'DELETE_SUCCESS':
		return {
			...state,
			loadingDelete: false,
			successDelete: true,
		};
	case 'DELETE_FAIL':
		return { ...state, loadingDelete: false, successDelete: false };
	case 'DELETE_RESET':
		return { ...state, loadingDelete: false, successDelete: false };
	default:
		return state;
	}
};

const normalizeConceptName = (name) => {
	return name
		.toLowerCase()
		.normalize('NFD')
		.replace(/[\u0300-\u036f]/g, '')
		.replace(/\s+/g, '-');
};

const hasValue = (value) => value !== undefined && value !== null && value !== '';

const renderEmpty = () => <span className='tw:text-ink-3'>—</span>;

const formatDate = (date) => {
	if (!date) {
		return null;
	}
	const parsed = new Date(date);
	if (Number.isNaN(parsed.getTime())) {
		return null;
	}
	return parsed.toLocaleDateString('es-UY', { timeZone: 'UTC' });
};

const formatAmount = (debt, value) => {
	if (!hasValue(value)) {
		return null;
	}
	const amount = Number(value);
	if (Number.isNaN(amount)) {
		return null;
	}
	const currency = showCurrency(debt.currency) || debt.currency || '';
	return `${currency} ${amount.toLocaleString('es-UY')}`.trim();
};

function DebtList() {
	const { t } = useLabels();
	const navigate = useNavigate();
	const { pathname, search } = useLocation();
	const sp = new URLSearchParams(search);
	const concept = sp.get('concept') || 'all';
	const currency = sp.get('currency') || 'all';
	const capitalTo = sp.get('capitalTo') || '';
	const capitalFrom = sp.get('capitalFrom') || '';
	const amountTo = sp.get('amountTo') || '';
	const amountFrom = sp.get('amountFrom') || 0;
	const dateFrom = sp.get('dateFrom') || '';
	const dateTo = sp.get('dateTo') || '';
	const endDateFrom = sp.get('endDateFrom') || '';
	const endDateTo = sp.get('endDateTo') || '';
	const query = sp.get('query') || 'all';
	const page = sp.get('page') || 1;
	const column = sp.get('column') || 'customId';
	const direction = sp.get('direction') || 'asc';

	const [concepts, setConcepts] = useState([]);
	const [conceptsLoading, setConceptsLoading] = useState(true);
	const [isOpen, setIsOpen] = useState(false);
	const [exportModalOpen, setExportModalOpen] = useState(false);
	const [{ loading, error, debts, successDelete, pageSize, countDebts }, dispatch] = useReducer(reducer, {
		debts: [],
		loading: true,
		error: '',
		pageSize: 0,
		countDebts: 0,
	});
	const initialLoading = loading && pageSize === 0;

	const [filterOptions, setFilterOptions] = useState({
		formType: 'Deudas',
		concept: concept,
		dateFrom: dateFrom,
		dateTo: dateTo,
		endDateFrom: endDateFrom,
		endDateTo: endDateTo,
		amountFrom: amountFrom,
		amountTo: amountTo,
		capitalFrom: capitalFrom,
		capitalTo: capitalTo,
		currency: currency,
		query: query,
	});

	const { canSee, canEdit, canCreate, canDelete } = usePermissions();

	const handleDropdownToggle = () => {
		setIsOpen(!isOpen);
	};

	const handleInputChange = (e) => {
		const { name, value } = e.target;
		setFilterOptions((prevOptions) => ({
			...prevOptions,
			[name]: value,
			formType: filterOptions.formType,
		}));
	};

	const getFilterUrl = (filter) => {
		const filterConcept = filter.concept || filterOptions.concept || concept;
		const filterQuery = filter.query || filterOptions.query || query;
		const filterPage = filter.page || 1;
		const filterDateFrom = filterOptions.dateFrom || '';
		const filterDateTo = filterOptions.dateTo || '';
		const filterEndDateFrom = filterOptions.endDateFrom || '';
		const filterEndDateTo = filterOptions.endDateTo || '';
		const filterAmountFrom = filterOptions.amountFrom || '';
		const filterAmountTo = filterOptions.amountTo || '';
		const filterCapitalFrom = filterOptions.capitalFrom || '';
		const filterCapitalTo = filterOptions.capitalTo || '';
		const filterCurrency = filterOptions.currency || '';

		const params = new URLSearchParams();
		params.append('page', filterPage);
		params.append('concept', filterConcept);
		params.append('query', filterQuery);
		params.append('dateFrom', filterDateFrom);
		params.append('dateTo', filterDateTo);
		params.append('endDateFrom', filterEndDateFrom);
		params.append('endDateTo', filterEndDateTo);
		params.append('amountFrom', filterAmountFrom);
		params.append('amountTo', filterAmountTo);
		params.append('capitalFrom', filterCapitalFrom);
		params.append('capitalTo', filterCapitalTo);
		params.append('currency', filterCurrency);

		return `/AdminScreen/debts/search?${params.toString()}`;
	};

	const handleConceptInputChange = (value) => {
		setFilterOptions((prevOptions) => ({
			...prevOptions,
			concept: value,
		}));
		navigate(getFilterUrl({ concept: value, page: 1 }));
	};

	const handleApplyFilter = () => {
		navigate(getFilterUrl({ page: 1 }));
	};

	const handleClearFilter = () => {
		setFilterOptions({
			formType: 'Deudas',
			concept: 'all',
			dateFrom: '',
			dateTo: '',
			endDateFrom: '',
			endDateTo: '',
			amountFrom: 0,
			amountTo: '',
			capitalFrom: '',
			capitalTo: '',
			currency: 'all',
			query: 'all',
		});
	};

	const onPageChange = (page) => {
		navigate(getFilterUrl({ page: page }));
	};

	useEffect(() => {
		const fetchDebtConcepts = async () => {
			try {
				const result = await DebtConceptService.list({ active: true });
				const activeConcepts = result.data.sort((a, b) => (a.order || 0) - (b.order || 0));
				const conceptOptions = [
					{ label: 'Todas', value: 'all' },
					...activeConcepts
						.filter((activeConcept) => activeConcept.name)
						.map((activeConcept) => ({
							label: activeConcept.name,
							value: normalizeConceptName(activeConcept.name),
						})),
					{ label: 'Duplicados', value: 'Duplicados' },
				];
				setConcepts(conceptOptions);
			} catch (error) {
				console.error('Error fetching debt concepts:', error);
				toast.error('No se pudieron cargar los conceptos');
			} finally {
				setConceptsLoading(false);
			}
		};

		fetchDebtConcepts();
	}, []);

	useEffect(() => {
		const fetchData = async () => {
			try {
				dispatch({ type: 'FETCH_REQUEST' });
				const { data } = await DebtService.search({
					page,
					concept,
					query,
					column,
					direction,
					currency,
					capitalFrom,
					capitalTo,
					amountFrom,
					amountTo,
					dateFrom,
					dateTo,
					endDateFrom,
					endDateTo,
				});
				dispatch({ type: 'FETCH_SUCCESS', payload: data });
			} catch (err) {
				dispatch({ type: 'FETCH_FAIL', payload: getError(err) });
				console.error(getError(err));
				toast.error('No se pudo obtener lista');
			}
		};

		if (successDelete) {
			dispatch({ type: 'DELETE_RESET' });
		}
		fetchData();
	}, [
		successDelete,
		concept,
		query,
		page,
		column,
		direction,
		currency,
		capitalTo,
		capitalFrom,
		amountTo,
		amountFrom,
		dateFrom,
		dateTo,
		endDateFrom,
		endDateTo,
	]);

	async function deleteRelatedPayments(debt) {
		let paymentDeleteOk;
		try {
			dispatch({ type: 'DELETE_REQUEST' });
			await PaymentService.deletePaymentsForDebt(debt._id);
			dispatch({ type: 'DELETE_SUCCESS' });
			paymentDeleteOk = true;
		} catch (err) {
			dispatch({ type: 'DELETE_FAIL' });
			paymentDeleteOk = false;
		}
		return paymentDeleteOk;
	}

	const handleSort = (column) => {
		const newSortDirection = column === column && direction === 'desc' ? 'asc' : 'desc';
		const newURL = `/AdminScreen/debts/search?page=${page}&concept=${concept}&query=${query}&column=${column}&direction=${newSortDirection}&currency=${currency}&capitalFrom=${capitalFrom}&capitalTo=${capitalTo}&amountFrom=${amountFrom}&amountTo=${amountTo}&dateFrom=${dateFrom}&dateTo=${dateTo}&endDateFrom=${endDateFrom}&endDateTo=${endDateTo}`;
		navigate(newURL);
	};

	async function deleteDebtHandler(debt) {
		if (window.confirm(t('msg.confirmDeleteDebt'))) {
			const paymentDeleteOk = await deleteRelatedPayments(debt);
			try {
				if (paymentDeleteOk) {
					dispatch({ type: 'DELETE_REQUEST' });
					await DebtService.delete(debt._id);
					dispatch({ type: 'DELETE_SUCCESS' });
					toast.success('deuda Eliminada');
				} else {
					toast.error('Error al eliminar la deuda debido a problemas con los pagos relacionados');
				}
			} catch (err) {
				dispatch({ type: 'DELETE_FAIL' });
				console.error(getError(err));
				toast.error('Error al eliminar cuenta');
			}
		}
	}

	async function editDebtHandler(debt) {
		navigate(`/AdminScreen/debts/editDebt/${debt._id}`);
	}

	async function seeDetails(debt) {
		navigate(`/AdminScreen/debts/debt/${debt._id}`);
	}

	async function newPaymentHandler() {
		// Without a returnTo, NewPayment.jsx falls back to the legacy
		// /AdminScreen/payments list — send the user back to this list instead.
		navigate('/AdminScreen/newPayment', { state: { returnTo: `${pathname}${search}` } });
	}

	async function addPaymentForDebtHandler(debt) {
		navigate(`/AdminScreen/debts/debt/${debt._id}/newPayment`);
	}

	// Superset of the on-screen debtTableColumns plus the `toExport`-only
	// fields from `debtsColumns` (utils.js) — `lastPaymentDate` is excluded,
	// it's `forReport: true` (Reports-screen only).
	const exportFields = [
		{ key: 'customId', label: t('field.accountConcept'), locked: true },
		{ key: 'owner.name', label: t('entity.account.debtor') },
		{ key: 'capital', label: t('field.capital') },
		{ key: 'concept', label: t('entity.concept') },
		{ key: 'dues', label: t('entity.due.plural') },
		{ key: 'amount', label: t('field.dueAmount') },
		{ key: 'date', label: t('field.date') },
		{ key: 'endDate', label: t('field.endDate') },
		{ key: 'periodicity', label: t('field.periodicity') },
		{ key: 'currency', label: t('field.currency') },
		{ key: 'previousDebt', label: t('field.previousDebt') },
		{ key: 'previousDebtDate', label: t('field.previousDebtDate') },
		{ key: 'interest', label: t('field.interestPercent') },
	];

	const exportFieldGetters = {
		customId: (debt) => debt.customId || '',
		'owner.name': (debt) => debt.owner?.name || '',
		capital: (debt) => formatAmount(debt, debt.capital) || '',
		concept: (debt) => debt.concept || '',
		dues: (debt) => debt.dues ?? '',
		amount: (debt) => formatAmount(debt, debt.amount) || '',
		date: (debt) => formatDate(debt.date) || '',
		endDate: (debt) => formatDate(debt.endDate) || '',
		periodicity: (debt) => (debt.periodicity ? parsePeriodicity(debt.periodicity.toString()) : ''),
		currency: (debt) => debt.currency || '',
		previousDebt: (debt) => formatAmount(debt, debt.previousDebt) || '',
		previousDebtDate: (debt) => formatDate(debt.previousDebtDate) || '',
		interest: (debt) => debt.interest ?? '',
	};

	async function handleExportDebts(selectedKeys) {
		const { data } = await DebtService.search({
			page: 1,
			concept,
			query,
			column,
			direction,
			currency,
			capitalFrom,
			capitalTo,
			amountFrom,
			amountTo,
			dateFrom,
			dateTo,
			endDateFrom,
			endDateTo,
			pageSize: countDebts || 1,
		});
		const orderedKeys = exportFields.map((f) => f.key).filter((key) => selectedKeys.includes(key));
		const rows = data.debts.map((debt) =>
			Object.fromEntries(
				orderedKeys.map((key) => [exportFields.find((f) => f.key === key).label, exportFieldGetters[key](debt)]),
			),
		);
		const ws = XLSX.utils.json_to_sheet(rows);
		const wb = XLSX.utils.book_new();
		XLSX.utils.book_append_sheet(wb, ws, 'Deudas');
		const buffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
		const blob = new Blob([buffer], { type: 'application/octet-stream' });
		return { blob, filename: `deudas_${new Date().toISOString().slice(0, 10)}.xlsx` };
	}

	const filterFormProps = {
		filterOptions: filterOptions,
		isOpen: isOpen,
		handleDropdownToggle: handleDropdownToggle,
		handleInputChange: handleInputChange,
		clearFilter: () => handleClearFilter(),
		handleApplyFilter: () => handleApplyFilter('Deudas'),
	};

	const renderMoney = (debt, value) => {
		const formatted = formatAmount(debt, value);
		return formatted ? (
			<span className='tw:font-mono tw:text-[13px] tw:tabular-nums'>{formatted}</span>
		) : (
			renderEmpty()
		);
	};

	const renderDate = (date) => {
		const formatted = formatDate(date);
		return formatted ? (
			<span className='tw:font-mono tw:text-[13px] tw:tabular-nums tw:text-ink-2'>{formatted}</span>
		) : (
			renderEmpty()
		);
	};

	const renderRowActions = (debt) => (
		<RowActionsMenu
			items={[
				canSee() && { label: 'Ver detalles', onClick: () => seeDetails(debt) },
				canEdit() && { label: 'Editar', onClick: () => editDebtHandler(debt) },
				canEdit() && { label: 'Agregar cobro', onClick: () => addPaymentForDebtHandler(debt) },
				canDelete() && { label: 'Eliminar', onClick: () => deleteDebtHandler(debt), danger: true },
			]}
		/>
	);

	const debtTableColumns = [
		{
			key: 'customId',
			header: t('field.accountConcept'),
			sortable: true,
			render: (debt) => (
				<div className='tw:flex tw:items-center tw:gap-2.5'>
					<IdTile name={debt.customId || debt.concept} />
					<div>
						<div className='tw:font-medium tw:text-ink-0'>{debt.customId || '—'}</div>
						{debt.owner?.name && (
							<div className='tw:text-[11.5px] tw:text-ink-3'>{debt.owner.name}</div>
						)}
					</div>
				</div>
			),
		},
		{
			key: 'owner.name',
			header: t('entity.account.debtor'),
			sortable: true,
			render: (debt) => {
				const ownerName = debt.owner?.name;
				return ownerName ? (
					<div className='tw:flex tw:items-center tw:gap-2'>
						<IdTile name={ownerName} size={22} shape='circle' />
						<span className='tw:text-[12.5px] tw:text-ink-2'>{ownerName}</span>
					</div>
				) : (
					renderEmpty()
				);
			},
		},
		{
			key: 'capital',
			header: t('field.capital'),
			sortable: true,
			className: 'tw:text-right',
			render: (debt) => renderMoney(debt, debt.capital),
		},
		{
			key: 'amount',
			header: t('field.dueAmount'),
			sortable: true,
			className: 'tw:text-right',
			render: (debt) => renderMoney(debt, debt.amount),
		},
		{
			key: 'dues',
			header: t('entity.due.plural'),
			sortable: true,
			className: 'tw:text-right',
			render: (debt) => hasValue(debt.dues) ? (
				<span className='tw:font-mono tw:text-[13px] tw:tabular-nums'>{debt.dues}</span>
			) : (
				renderEmpty()
			),
		},
		{
			key: 'date',
			header: t('field.date'),
			sortable: true,
			render: (debt) => renderDate(debt.date),
		},
		{
			key: 'endDate',
			header: t('field.endDate'),
			sortable: true,
			render: (debt) => renderDate(debt.endDate),
		},
		{
			key: 'periodicity',
			header: t('field.periodicity'),
			sortable: true,
			render: (debt) => debt.periodicity ? (
				<span className='tw:text-[12.5px] tw:text-ink-2'>{parsePeriodicity(debt.periodicity.toString())}</span>
			) : (
				renderEmpty()
			),
		},
		{
			key: 'currency',
			header: t('field.currency'),
			sortable: true,
			className: 'tw:text-right',
			render: (debt) => debt.currency ? (
				<span className='tw:font-mono tw:text-[13px] tw:tabular-nums'>{debt.currency}</span>
			) : (
				renderEmpty()
			),
		},
		{
			key: 'concept',
			header: t('entity.concept'),
			sortable: true,
			render: (debt) => debt.concept ? <Pill tone='accent'>{debt.concept}</Pill> : renderEmpty(),
		},
	];

	return (
		<div>
			<Helmet>
				<title>{`Lista de ${t('entity.debt.plural')}`}</title>
			</Helmet>

			<FilterForm {...filterFormProps} />

			<EntityList>
				<PageHead
					breadcrumb={[{ label: 'Inicio', to: '/AdminScreen' }, { label: t('entity.debt.plural') }]}
					title={t('entity.debt.plural')}
					subtitle={`${initialLoading ? '—' : countDebts} ${t('entity.debt.plural').toLowerCase()} encontradas`}
					actions={
						<>
							<Button variant='default' icon={<FaFileExcel />} onClick={() => setExportModalOpen(true)}>
								Exportar
							</Button>
							{canCreate() && (
								<Button variant='primary' icon={<FaPlus />} onClick={() => navigate('/AdminScreen/newDebt')}>
									{`Agregar ${t('entity.debt').toLowerCase()}`}
								</Button>
							)}
							{canEdit() && (
								<Button icon={<MdOutlineMoney />} onClick={newPaymentHandler}>
									Agregar cobro
								</Button>
							)}
							<Button onClick={() => navigate('/AdminScreen/debts/pendingCounts')}>
								Cuotas por confirmar
							</Button>
						</>
					}
				/>
				<ListToolbar
					query={query}
					getFilterUrl={getFilterUrl}
					navigate={navigate}
					searchPlaceholder='Buscar por deuda, deudor, concepto o fecha…'
					segments={concepts}
					activeSegment={filterOptions.concept}
					onSegmentChange={handleConceptInputChange}
					segmentsLoading={conceptsLoading}
					onAdvancedFilter={handleDropdownToggle}
					resultCount={initialLoading ? null : countDebts}
				/>
				<DataTable
					columns={debtTableColumns}
					rows={debts}
					loading={loading}
					skeleton
					skeletonRows={pageSize || 15} // 15 matches routes/debtRoutes.js PAGE_SIZE
					error={error}
					sortColumn={column}
					direction={direction}
					onSort={handleSort}
					onRowClick={(debt) => seeDetails(debt)}
					rowActions={canEdit() || canDelete() ? renderRowActions : undefined}
					emptyMessage='No se encontraron deudas con los filtros aplicados.'
				/>
				<Paginator
					className='tw:mt-4'
					totalCount={countDebts}
					pageSize={pageSize}
					currentPage={parseInt(page)}
					onPageChange={onPageChange}
				/>
			</EntityList>

			<ExportModal
				show={exportModalOpen}
				onClose={() => setExportModalOpen(false)}
				count={countDebts}
				fields={exportFields}
				subtitle={`${t('entity.debt.plural')} — se exportará el listado filtrado actual`}
				onExport={handleExportDebts}
			/>
		</div>
	);
}

export default DebtList;
